import React from 'react';

// Visual treatment per match category.
const STYLES = {
    glossary: { textDecoration: 'underline', textDecorationColor: 'var(--ok, #1a7f37)', textDecorationStyle: 'solid', cursor: 'help' },
    doNotTranslate: { textDecoration: 'underline dotted', textDecorationColor: 'var(--accent, #2563eb)', cursor: 'help' },
    forbidden: { textDecoration: 'underline wavy', textDecorationColor: 'var(--danger, #d1242f)', background: 'rgba(209,36,47,0.06)', cursor: 'help' },
    preferred: { textDecoration: 'underline wavy', textDecorationColor: 'var(--warn, #d4a72c)', background: 'rgba(212,167,44,0.08)', cursor: 'help' },
};

function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build a matcher set from glossary terms + brand voice. Each matcher is
 * { phrase, type, title }. Phrases are matched case-insensitively against the
 * target reading text. Longer phrases win on overlap (sorted desc).
 */
export function buildMatchers({ glossaryTerms = [], brandVoice = null }) {
    const matchers = [];

    for (const t of glossaryTerms) {
        if (t.ignore) continue;
        const phrase = (t.target || '').trim();
        if (!phrase) continue;
        const ctx = t.context ? ` — ${t.context}` : '';
        matchers.push({ phrase, type: 'glossary', title: `Glossary: ${t.source} → ${t.target}${ctx}` });
    }

    if (brandVoice) {
        for (const w of brandVoice.doNotTranslate || []) {
            const phrase = (w || '').trim();
            if (phrase) matchers.push({ phrase, type: 'doNotTranslate', title: `Do not translate: ${phrase}` });
        }
        for (const f of brandVoice.forbidden || []) {
            const phrase = (f.term || '').trim();
            if (phrase) matchers.push({ phrase, type: 'forbidden', title: `Forbidden${f.reason ? `: ${f.reason}` : ''}` });
        }
        for (const p of brandVoice.preferred || []) {
            const phrase = (p.avoid || '').trim();
            if (phrase) matchers.push({ phrase, type: 'preferred', title: `Prefer "${p.use}" instead of "${p.avoid}"` });
        }
    }

    // Longest first so multi-word phrases take precedence over their substrings.
    matchers.sort((a, b) => b.phrase.length - a.phrase.length);
    return matchers;
}

// Compile matchers into a single regex (cached on the array identity by caller).
function compile(matchers) {
    if (!matchers.length) return null;
    const alt = matchers.map((m) => escapeRegExp(m.phrase)).join('|');
    // Word-ish boundaries: avoid matching inside longer words for ascii terms,
    // but still match phrases with spaces/punctuation.
    return new RegExp(`(${alt})`, 'gi');
}

/**
 * Render text with matched phrases wrapped in styled spans. Returns the plain
 * string when there are no matchers (cheap path for the common case).
 */
export function highlightText(text, matchers) {
    if (!text || !matchers || !matchers.length) return text;
    const re = compile(matchers);
    if (!re) return text;

    const lower = text.toLowerCase();
    const byPhrase = new Map(matchers.map((m) => [m.phrase.toLowerCase(), m]));

    const out = [];
    let last = 0;
    let match;
    let k = 0;
    while ((match = re.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        if (start > last) out.push(text.slice(last, start));
        const meta = byPhrase.get(lower.slice(start, end)) || { type: 'glossary', title: match[0] };
        out.push(
            <span key={`h${k++}`} style={STYLES[meta.type]} title={meta.title}>
                {match[0]}
            </span>,
        );
        last = end;
        if (re.lastIndex === start) re.lastIndex++; // guard against zero-width
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}
